import * as React from 'react';
import { connectForm, IFormProps } from 'form-container';
import Button from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';
import TextInput, { Addon } from '../Inputs/TextInput/TextInput';
import DateInput from '../Inputs/DateInput/DateInput';
import SwitchInput from '../Inputs/SwitchInput/SwitchInput';
import DropdownInput from '../Inputs/DropdownInput/DropdownInput';
import Loading from '../Loading/Loading';
import OutputDetail, { OutputDetailProps } from '../Outputs/OutputDetail/OutputDetail';
import BreakDown, { BreakDownProps } from '../Outputs/BreakDown/BreakDown';
import ImportantInfo from '../Outputs/ImportantInfo/ImportantInfo';
import validators from '../../utilities/validators';
import {
  sectionWrapperContent,
  Gender,
  Plan,
  Country,
  Frequency,
  IsCash,
  analytics,
} from '../../content/content';
import { calculate } from '../../utilities/calculator';
import { renderDataTest } from '../../utilities/testAutomation';
import Tiles, { retirementOptionsTiles } from '../Tiles/Tiles';

const Parser = require('html-react-parser');

const {
  isRequired,
  isNumber,
  isLessThanOneMillion,
  isDateValid,
  isOver18,
  isAfter1900,
  isInThePast,
  isPercentage,
  isMoreThan0,
} = validators;

interface IProps extends IFormProps {
  isTesting: boolean;
}
interface IState {
  shouldResultsShow: boolean;
  isRecalculating: boolean;
  results: ResultsModel;
}

interface ResultsModel {
  detail: OutputDetailProps;
  tables: BreakDownProps;
}

export interface InputModel {
  gender: Gender;
  birthDate: string;
  residence: Country;
  plan: Plan;
  planValue: string;
  isCash: IsCash;
  incomeFrequency: Frequency;
  currentIncome: string;
  plannedIncome: string;
  lumpsumType: Addon;
  lumpsum: string;
}

class SectionWrapper extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      shouldResultsShow: false,
      isRecalculating: false,
      results: {
        detail: {
          incomeFrequency: '',
          currentIncome: '',
          plannedIncome: '',
          lumpsum: '',
          timeFrame: '',
        },
        tables: {
          planned: {
            amount: '',
            percentage: '',
            willLast: '',
            whenYouAre: '',
          },
          current: {
            amount: '',
            percentage: '',
            willLast: '',
            whenYouAre: '',
          },
          yearsFromDeath: '',
          lifeExpectancy: '',
        },
      },
    };
  }

  shouldComponentUpdate(nextProps: IProps, nextState: IState) {
    if (this.state.isRecalculating !== nextState.isRecalculating) {
      return !nextState.isRecalculating;
    }
    return true;
  }

  private outputSection: HTMLDivElement;

  render() {
    const {
      form: {
        touched,
        validationErrors,
        model,
      }, formMethods: { bindInput },
      isTesting,
    } = this.props;
    let { form: { isValid } } = this.props;
    const { shouldResultsShow, isRecalculating, results } = this.state;
    const dataTest = renderDataTest(isTesting);

    const handleClick = () => {
      this.setState({
        shouldResultsShow: true,
        isRecalculating: false,
        results: calculate(model),
      });
      setTimeout(() => { this.outputSection.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    };

    if (shouldResultsShow && !isRecalculating) {
      this.setState({ isRecalculating: true });
      isValid = false;
    }

    return (
      <div className="section-wrapper">
        <div className="input-section hide-print">
          <div className="grid grid--flex-cells md--up">
            <div className="cell">
              <div className="section">
                <div className="header">
                  <h3>{sectionWrapperContent.aboutYouTitle}</h3>
                  <Tooltip
                    content={sectionWrapperContent.aboutYouTooltip}
                    dataTest={dataTest('soi-about-you-tooltip')}
                  />
                </div>
                <div className="body">
                  <div className="grid grid--col sm--up">
                    <div className="cell--inner label">
                      <p>{sectionWrapperContent.genderLabel}</p>
                    </div>
                    <div className="cell--inner">
                      <SwitchInput
                        options={[Gender.Male, Gender.Female]}
                        dataTest={{ isTesting, tag: 'soi-about-you-button-gender' }}
                        {...bindInput('gender')}
                      />
                    </div>
                  </div>
                  <div className="grid grid--col sm--up">
                    <div className="cell--inner label">
                      <p>{sectionWrapperContent.birthDateLabel}</p>
                    </div>
                    <div className="cell--inner">
                      <DateInput
                        error={(touched.birthDate && validationErrors.birthDate)}
                        dataTest={{ isTesting, tag: 'soi-about-you-input-dob' }}
                        {...bindInput('birthDate')}
                      />
                    </div>
                  </div>
                  <div className="grid grid--col sm--up">
                    <div className="cell--inner label">
                      <p>{sectionWrapperContent.residenceLabel}</p>
                    </div>
                    <div className="cell--inner">
                      <DropdownInput
                        options={[Country.England, Country.Scotland, Country.NorthernIreland, Country.Wales]}
                        label={sectionWrapperContent.residenceDropdownLabel}
                        dataTest={dataTest('soi-about-you-input-country')}
                        {...bindInput('residence')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="cell">
              <div className="section">
                <div className="header">
                  <h3>{sectionWrapperContent.yourPlanTitle}</h3>
                  <Tooltip
                    content={sectionWrapperContent.yourPlanTooltip}
                    dataTest={dataTest('soi-your-plan-tooltip')}
                  />
                </div>
                <div className="body">
                  <div className="grid--col offset sm--up">
                    <div className="cell--inner label">
                      <p>{sectionWrapperContent.planLabel}</p>
                    </div>
                    <div className="cell--inner">
                      <DropdownInput
                        options={[Plan.FRP, Plan.PCP, Plan.RA, Plan.OTHER]}
                        label={sectionWrapperContent.planDropdownLabel}
                        dataTest={dataTest('soi-your-plan-input-plan')}
                        {...bindInput('plan')}
                      />
                    </div>
                  </div>
                  <div className="grid--col sm--up">
                    <div className="cell--inner label">
                      <p>{sectionWrapperContent.planValueLabel}</p>
                    </div>
                    <div className="cell--inner">
                      <TextInput
                        error={touched.planValue && validationErrors.planValue}
                        dataTest={{ isTesting, tag: 'soi-your-plan-input-plan-value' }}
                        {...bindInput('planValue')}
                      />
                    </div>
                  </div>
                  <div className="grid--col sm--up">
                    <div className="cell--inner label">
                      <p>{sectionWrapperContent.cashLabel}</p>
                    </div>
                    <div className="cell--inner">
                      <SwitchInput
                        options={[IsCash.Yes, IsCash.No]}
                        dataTest={{ isTesting, tag: 'soi-your-plan-button-cash' }}
                        {...bindInput('isCash')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid md--up">
            <div className="cell">
              <div className="section">
                <div className="header">
                  <h3>{sectionWrapperContent.changesToIncomeTitle}</h3>
                  <Tooltip
                    content={sectionWrapperContent.changesToIncomeTooltip}
                    dataTest={dataTest('soi-changes-to-income-tooltip')}
                  />
                </div>
                <div className="body">
                  <div className="grid md--up">
                    <div className="flex-1">
                      <div className="grid--col -no-border sm--up margin-bottom-none">
                        <div className="cell--inner label">
                          <p style={{ visibility: 'hidden' }}>Switch</p>
                        </div>
                        <div className="cell--inner">
                          <SwitchInput
                            options={[Frequency.Monthly, Frequency.Yearly]}
                            dataTest={{ isTesting, tag: 'soi-changes-to-income-button-frequency' }}
                            {...bindInput('incomeFrequency')}
                          />
                        </div>
                      </div>
                      <div className="grid--col sm--up">
                        <div className="cell--inner label">
                          <p>{sectionWrapperContent.currentIncomeLabel}</p>
                        </div>
                        <div className="cell--inner">
                          <TextInput
                            error={touched.currentIncome && validationErrors.currentIncome}
                            dataTest={{ isTesting, tag: 'soi-changes-to-income-input-current-drawdown' }}
                            {...bindInput('currentIncome')}
                          />
                        </div>
                      </div>
                      <div className="grid--col sm--up">
                        <div className="cell--inner label">
                          <p>{sectionWrapperContent.plannedIncomeLabel}</p>
                        </div>
                        <div className="cell--inner">
                          <TextInput
                            error={touched.plannedIncome && validationErrors.plannedIncome}
                            dataTest={{ isTesting, tag: 'soi-changes-to-income-input-planned-drawdown' }}
                            {...bindInput('plannedIncome')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="grid--col lump-sum sm--up">
                        <div>
                          <div className="cell--inner label">
                            <p>{sectionWrapperContent.lumpsumLabel}</p>
                          </div>
                          <div className="cell--inner">
                            <SwitchInput
                              options={[Addon.Pound, Addon.Percentage]}
                              dataTest={{ isTesting, tag: 'soi-changes-to-income-button-lump-sum-type' }}
                              {...bindInput('lumpsumType')}
                            />
                          </div>
                        </div>
                        <div className="cell--inner">
                          <TextInput
                            error={touched.lumpsum && validationErrors.lumpsum}
                            placeholder={!model.lumpsumType ? sectionWrapperContent.lumpsumPlaceholder : undefined}
                            addon={model.lumpsumType}
                            dataTest={{ isTesting, tag: 'soi-changes-to-income-input-lump-sum-value' }}
                            {...bindInput('lumpsum')}
                          />
                        </div>
                      </div>
                      <div className="info-text" {...dataTest('soi-changes-to-income-info-text')}>
                        {Parser(model.isCash ? sectionWrapperContent[`infoText${model.isCash}`] : '')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid md--up">
            <div className="cell align-center">
              <Button
                name={!shouldResultsShow ?
                  sectionWrapperContent.calculateButton :
                  sectionWrapperContent.updateButton}
                onClick={handleClick}
                isDisabled={!isValid}
                analytics={{
                  id: analytics.buttonsId,
                  data: !shouldResultsShow ? analytics.calculate : analytics.update,
                }}
                dataTest={dataTest(!shouldResultsShow ? 'soi-button-calculate' : 'soi-button-update-results')}
              />
            </div>
          </div>
        </div>
        {shouldResultsShow && results &&
          <div className="output-section show-print" ref={(output: HTMLDivElement) => { this.outputSection = output; }}>
            <div className="output-section--white">
              <div className="output-section__container">
                {!isRecalculating && <Loading />}
                <OutputDetail
                  incomeFrequency={results.detail.incomeFrequency}
                  currentIncome={results.detail.currentIncome}
                  plannedIncome={results.detail.plannedIncome}
                  lumpsum={results.detail.lumpsum}
                  timeFrame={results.detail.timeFrame}
                />
                <BreakDown
                  planned={results.tables.planned}
                  current={results.tables.current}
                  yearsFromDeath={results.tables.yearsFromDeath}
                  lifeExpectancy={results.tables.lifeExpectancy}
                />
                <ImportantInfo planType={model.plan} dataTest={dataTest('soi-important-info-button-print')} />
              </div>
            </div>
            <div className="output-section__container">
              <div className="output-section__inner-container">
                <Tiles single={model.plan === Plan.RA || model.plan === Plan.OTHER} items={model.plan === Plan.RA || model.plan === Plan.OTHER ? [retirementOptionsTiles[1]] : retirementOptionsTiles} planType={model.plan} />
              </div>
            </div>
          </div>
        }
      </div>
    );
  }
}

const nonTextValidators = [
  isRequired('gender'),
  isRequired('incomeFrequency'),
  isRequired('residence'),
  isRequired('plan'),
  isRequired('isCash'),
  isMoreThan0('planValue'),
  isOver18('birthDate'),
  isAfter1900('birthDate'),
  isInThePast('birthDate'),
  isDateValid('birthDate'),
  isRequired('birthDate'),
  isRequired('lumpsum', (model: any) => !!model.lumpsumType),
  isRequired('lumpsumType', (model: any) => !!model.lumpsum),
  isPercentage('lumpsum', (model: any) => model.lumpsumType === Addon.Percentage),
  isLessThanOneMillion('lumpsum', (model: any) => model.lumpsumType === Addon.Pound),
  isNumber('lumpsum'),
];
const moneyValidators = [
  'planValue',
  'currentIncome',
  'plannedIncome',
].map((input: string) => [
  isLessThanOneMillion(input),
  isNumber(input),
  isRequired(input),
]);
const formValidators = nonTextValidators.concat(...moneyValidators);

export default connectForm(formValidators)(SectionWrapper);



// WEBPACK FOOTER //
// ./src/components/SectionWrapper/SectionWrapper.tsx